// SettingsView — the stateful harness that turns the pure Settings panels into
// a live, mountable surface. This is the ONE stateful seam over Settings: the
// SettingsLayout + AccountProfile/PreferencesPanel children stay pure views;
// every piece of state they report UP is held here.
//
// This is a legitimate impure-shell composition root (a mount wrapper), so it
// MAY hold React state — and nothing more. It owns:
//   - `section`      — which section the nav has selected;
//   - `notice`       — which transient outcome toast (if any) is showing.
// The IDENTITY is not owned here: the account and workspace arrive as REQUIRED
// props (real data, never fixture defaults — ui-master-audit §3 S1), and each
// display-name edit is reported UP for the host to persist.
// NO `as` casts anywhere.
//
// Theme applies on change, GLOBALLY: the theme axis is owned by ThemeProvider
// (app/theme.tsx), which stamps `data-theme` on the document root so the whole
// app re-themes — not just this pane. This harness reads/writes it via
// `useTheme()`; it holds no local theme state and stamps no wrapper attribute.

import { appName } from "@bilda/app/identity";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { buildInfo, versionWithBuild } from "../../app/buildInfo.js";
import { useCopy } from "../../app/copy.js";
import { useTheme } from "../../app/theme.js";
import { isLocale, setLocale } from "../../i18n/setup.js";
import {
    type IamManagementPorts,
    IamWorkspaceHost,
} from "../Iam/IamWorkspaceHost.js";
import { Icon } from "../Icon/Icon.js";
import { AboutPanel } from "../Settings/AboutPanel/AboutPanel.js";
import { AccountProfile } from "../Settings/AccountProfile/AccountProfile.js";
import {
    AdminAccountsSection,
    WorkspaceDataSection,
} from "../Settings/AdminPanel/AdminPanel.js";
import { AssistantPanel } from "../Settings/AssistantPanel/AssistantPanel.js";
import { PreferencesPanel } from "../Settings/PreferencesPanel/PreferencesPanel.js";
import { SettingsLayout } from "../Settings/SettingsLayout/SettingsLayout.js";
import type {
    AccountIdentity,
    LanguagePreference,
    SettingsNavItem,
    SettingsSectionKind,
} from "../Settings/types.js";
import {
    Toast,
    ToastProvider,
    type ToastTone,
    ToastViewport,
} from "../Toast/Toast.js";

// A transient outcome the harness surfaces as a toast. A named union (not a
// meaning-swapping flag): each outcome is its own distinct kind, so the toast
// content maps totally from it.
type SettingsNotice = "workspace_log_exported";

// === Section-nav glyphs (decorative — the label names each section). ===
// Kept in step with the SettingsLayout render stories so the assembled surface
// reads identically whether reached from a story or from the AppShell mock.
const personGlyph = <Icon name="person" />;
const slidersGlyph = <Icon name="sliders" />;
const sparkGlyph = <Icon name="sparkle" />;
const workspaceGlyph = <Icon name="workspace" />;
const aboutGlyph = <Icon name="info" />;

export type SettingsViewProps = {
    // The real identity. REQUIRED — the account section renders exactly what
    // the caller knows to be true: the authenticated sign-in email plus the
    // per-device display name (or `pending` before the auth probe resolves).
    readonly identity: AccountIdentity;
    // Each display-name edit, reported UP; the caller owns persistence.
    readonly onDisplayNameChange: (value: string) => void;
    // The editable workspace name (F56) — the RAW value ("" = unnamed) and
    // the derived display default as placeholder. Each edit reported UP; the
    // caller dispatches the `renameWorkspace` command.
    readonly workspaceName: string;
    readonly workspaceNamePlaceholder: string;
    readonly onWorkspaceNameChange: (value: string) => void;
    // The semantic-summary consent trust surface (IDD 94442b9d) — a real
    // command dispatch (`setSummaryConsent`), device-mirrored by the caller
    // (`app/summaryConsent.ts`) since `GET /state` doesn't read the domain
    // value back yet.
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;
    // The journal's own-text settings, shown in the Assistant section
    // (see AssistantPanel). Omit → not shown.
    readonly journalTexts?: ReactNode;
    // Which section is shown first.
    readonly initialSection?: SettingsSectionKind;
    // IAM: the signed-in identity + the control-plane ports the "Workspace"
    // section needs. `undefined` only while the auth probe hasn't resolved
    // yet — the section is then omitted entirely. The app is always
    // authenticated once the probe lands.
    readonly iamIdentity?: { readonly userId: string; readonly email: string };
    readonly iamPorts?: IamManagementPorts;
    // Sign out (the shell owns it: server sign-out, then a fresh boot).
    readonly onSignOut: () => void;
    // Workspace-data export, rendered inside the "Workspace" section below
    // IamWorkspaceHost. Triggers the browser download of the full event-log
    // export (`GET /account/export`); the actual DOM I/O lives with the
    // host's other download affordances (reuses the same
    // `ports.downloadExport` the package-export flow already uses), so this
    // harness stays free of a raw DOM dependency. `undefined` → the Export
    // row is omitted (the auth probe hasn't resolved).
    readonly onExportWorkspaceLog?: () => void;
    // The account-administration page, set only for an admin account
    // (FR-12.2). `undefined` → the Administration row is omitted.
    readonly adminAccountsHref?: string;
};

export const SettingsView = ({
    identity,
    onDisplayNameChange,
    workspaceName,
    workspaceNamePlaceholder,
    onWorkspaceNameChange,
    summaryConsent,
    onSummaryConsentChange,
    journalTexts,
    initialSection = "account",
    iamIdentity,
    iamPorts,
    onSignOut,
    onExportWorkspaceLog,
    adminAccountsHref,
}: SettingsViewProps) => {
    const { t, i18n } = useLingui();
    const copy = useCopy();

    const [section, setSection] = useState<SettingsSectionKind>(initialSection);
    // Theme AND language are GLOBAL, per-device preferences held OUTSIDE this
    // pane (memory project_theme_outside_event_log): a change must re-theme /
    // re-localize the WHOLE app, and survive reload — so neither is local state.
    //   - theme    → ThemeProvider stamps `data-theme` on the document root;
    //     read/written via useTheme().
    //   - language → the i18n seam (i18n/setup.ts) activates AND persists the
    //     locale; the picked locale is derived at startup from the OS/UI locale
    //     (detectLocale) UNLESS a persisted override exists. Here we READ the
    //     active locale (`i18n.locale`, narrowed at the boundary — no `as`) and
    //     WRITE through `setLocale`, which activates + persists in one step.
    const { palette, setTheme } = useTheme();
    const language: LanguagePreference = isLocale(i18n.locale)
        ? i18n.locale
        : "en";
    const [notice, setNotice] = useState<SettingsNotice | null>(null);

    // Whether the signed-in identity is known yet — false only while the auth
    // probe hasn't resolved, which hides the Workspace section. There's no
    // anonymous case here: AppShellHost's sign-in gate keeps an anonymous
    // session from ever reaching Settings.
    const hasIamIdentity = iamIdentity !== undefined && iamPorts !== undefined;
    // Workspace: "who you are + who else is here" (IamWorkspaceHost) plus
    // the one workspace-OPERATIONS row left, exporting the event log
    // (`WorkspaceDataSection` below) — see the panel for why Integrations
    // and the second delete button are gone. Placed right after Account so
    // the two read as neighbours.
    const workspaceNav: readonly SettingsNavItem[] = hasIamIdentity
        ? [{ kind: "workspace", label: t`Workspace`, icon: workspaceGlyph }]
        : [];
    const sections: readonly SettingsNavItem[] = [
        { kind: "account", label: t`Account`, icon: personGlyph },
        ...workspaceNav,
        { kind: "preferences", label: t`Preferences`, icon: slidersGlyph },
        { kind: "assistant", label: t`Assistant`, icon: sparkGlyph },
        { kind: "about", label: t`About`, icon: aboutGlyph },
    ];

    // kind → panel: a total mapping, so adding a section forces a panel here.
    const panels: Record<SettingsSectionKind, ReactNode> = {
        about: (
            <AboutPanel
                appName={appName}
                version={versionWithBuild(buildInfo)}
                onCopyVersion={
                    copy === null ? undefined : () => copy({ type: "version" })
                }
            />
        ),
        account: (
            <AccountProfile
                identity={identity}
                onDisplayNameChange={onDisplayNameChange}
                workspaceName={workspaceName}
                workspaceNamePlaceholder={workspaceNamePlaceholder}
                onWorkspaceNameChange={onWorkspaceNameChange}
            />
        ),
        preferences: (
            <PreferencesPanel
                // The control offers only Light/Dark (System lives in the
                // left menu's Theme row); on System it shows the palette the
                // app paints now, so one segment is always lit. Picking
                // either writes back an explicit light/dark via `setTheme`.
                values={{ theme: palette, language }}
                onThemeChange={setTheme}
                onLanguageChange={setLocale}
            />
        ),
        assistant: (
            <AssistantPanel
                summaryConsent={summaryConsent}
                onSummaryConsentChange={onSummaryConsentChange}
                journalTexts={journalTexts}
            />
        ),
        // The full IAM host (switcher/members/invite/sign-out/delete-account)
        // plus the one surviving workspace-OPERATIONS row (export the event
        // log — the Danger Zone's "Delete workspace" and the whole
        // Integrations section were cut: the delete merged into
        // IamWorkspaceHost's own "Delete account" above, since both fired the
        // identical `POST /account/delete` cascade, and Integrations had no
        // backing state to begin with — see
        // .claude/analysis/settings-simplification-2026-09-22.md §2/§3). Null
        // only before the auth probe resolves; covers the (should-never-
        // happen) case of a stale `initialSection="workspace"` arriving
        // before the identity resolves.
        workspace:
            iamIdentity !== undefined && iamPorts !== undefined ? (
                <>
                    <IamWorkspaceHost
                        userId={iamIdentity.userId}
                        email={iamIdentity.email}
                        ports={iamPorts}
                        onSignOut={onSignOut}
                    />
                    {onExportWorkspaceLog !== undefined ? (
                        <WorkspaceDataSection
                            onExportWorkspaceLog={() => {
                                onExportWorkspaceLog();
                                setNotice("workspace_log_exported");
                            }}
                        />
                    ) : null}
                    {adminAccountsHref !== undefined ? (
                        <AdminAccountsSection href={adminAccountsHref} />
                    ) : null}
                </>
            ) : null,
    };

    // notice → toast content: a total mapping, so a new outcome forces its copy.
    const noticeContent: Record<
        SettingsNotice,
        {
            readonly tone: ToastTone;
            readonly title: string;
            readonly description: string;
        }
    > = {
        workspace_log_exported: {
            tone: "success",
            title: t`Downloading a copy`,
            description: t`The download should start shortly.`,
        },
    };

    return (
        <ToastProvider>
            <SettingsLayout
                sections={sections}
                activeKind={section}
                onSectionSelect={setSection}
                title={t`Settings`}
                navAriaLabel={t`Settings sections`}
            >
                {panels[section]}
            </SettingsLayout>

            {notice !== null ? (
                <Toast
                    open
                    onOpenChange={(open) => {
                        if (!open) setNotice(null);
                    }}
                    tone={noticeContent[notice].tone}
                    title={noticeContent[notice].title}
                    description={noticeContent[notice].description}
                />
            ) : null}
            <ToastViewport />
        </ToastProvider>
    );
};
